import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dayjs } from 'src/libs/dayjs';
import { Campaign, Media, TimePeriod } from 'src/types';
import { ModelVersion } from './useModelVersions';

type GoalNavigationState = {
  filteredByGoal?: string;
  modelParametersId?: string;
  timeRange?: {
    start: string;
    end: string;
  };
  mediaChannels?: string[];
  campaigns?: number[];
};

type UseGoalNavigationFiltersParams = {
  modelVersions: ModelVersion[];
  media: Media[];
  campaigns: Campaign[];
  setCurrentModelVersion: (model: ModelVersion | null) => void;
  updateTimeRange: (
    timePeriod: TimePeriod,
    start?: ReturnType<typeof dayjs>,
    end?: ReturnType<typeof dayjs>,
  ) => void;
  setMedia: (media: Media[]) => void;
  setCampaigns: (campaigns: Campaign[]) => void;
  setTimeRangeLocked: (locked: boolean) => void;
  setMediaLocked: (locked: boolean) => void;
  setCampaignsLocked: (locked: boolean) => void;
  isReady: boolean;
};

export const useGoalNavigationFilters = ({
  modelVersions,
  media,
  campaigns,
  setCurrentModelVersion,
  updateTimeRange,
  setMedia,
  setCampaigns,
  setTimeRangeLocked,
  setMediaLocked,
  setCampaignsLocked,
  isReady,
}: UseGoalNavigationFiltersParams) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastAppliedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const state = location.state as GoalNavigationState | null;
    const hasGoalState = !!state?.filteredByGoal;
    const currentKey = location.key;

    // Don't apply if no goal state or not ready
    if (!hasGoalState || !isReady) {
      return;
    }

    // Skip if already applied for this navigation (same location.key)
    if (lastAppliedKeyRef.current === currentKey) {
      return;
    }

    // Apply model
    if (state.modelParametersId && modelVersions.length > 0) {
      const model = modelVersions.find(
        (m) => m.modelParametersId === state.modelParametersId,
      );
      if (model) {
        setCurrentModelVersion(model);
      }
    }

    // Apply time range and lock it
    if (state.timeRange?.start && state.timeRange?.end) {
      updateTimeRange(
        TimePeriod.CUSTOM,
        dayjs(state.timeRange.start),
        dayjs(state.timeRange.end),
      );
      setTimeRangeLocked(true);
    }

    // Apply media channels filter (only if there are matches) and lock
    if (
      state.mediaChannels &&
      state.mediaChannels.length > 0 &&
      media.length > 0
    ) {
      const mediaChannelsSet = new Set(state.mediaChannels);
      const hasMatches = media.some((m) => mediaChannelsSet.has(m.name));

      if (hasMatches) {
        const updatedMedia = media.map((m) => ({
          ...m,
          selected: mediaChannelsSet.has(m.name),
        }));
        setMedia(updatedMedia);
        setMediaLocked(true);
      }
    }

    // Apply campaigns filter (only if there are matches) and lock
    if (state.campaigns && state.campaigns.length > 0 && campaigns.length > 0) {
      const campaignIdsSet = new Set(state.campaigns.map(String));
      const hasMatches = campaigns.some((c) => campaignIdsSet.has(c.id));

      if (hasMatches) {
        const updatedCampaigns = campaigns.map((c) => ({
          ...c,
          selected: campaignIdsSet.has(c.id),
        }));
        setCampaigns(updatedCampaigns);
        setCampaignsLocked(true);
      }
    }

    // Mark as applied
    lastAppliedKeyRef.current = currentKey;

    // Clear state to prevent URL state pollution
    navigate(location.pathname, { replace: true, state: null });
  }, [
    location.state,
    location.pathname,
    location.key,
    modelVersions,
    media,
    campaigns,
    setCurrentModelVersion,
    updateTimeRange,
    setMedia,
    setCampaigns,
    setTimeRangeLocked,
    setMediaLocked,
    setCampaignsLocked,
    navigate,
    isReady,
  ]);
};
